import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import googleCalendarWordmark from "./assets/google-calendar-wordmark-174.svg";
import googleCalendarIcon from "./assets/google-calendar.png";
import {
  countries,
  findCountryByCode,
  validatePhoneNumber,
  getPhoneLengthHint,
} from "./countryPhoneData";
import {
  formatTimezoneLabel,
  GCalChevronLeft,
  GCalChevronRight,
  GCalIconButton,
  GCalSlotButton,
} from "./gcalUiHelpers";
import { useGcalOfficialStyles } from "./useGcalOfficialStyles";
import { BOOKING_STORAGE_KEY } from "./clientSession";
const EVENT_TITLE = "30 Minute Phone Call";
const ORGANIZER_NAME = "Kelsey Lee";
const BOOKING_LEAD_DAYS = 7;
const SLOT_MINUTES = 30;
const DAY_START_MINUTES = 11 * 60;
const DAY_END_MINUTES = 14 * 60 + 30;
const HOST_AVATAR =
  "https://media.licdn.com/dms/image/v2/D5603AQGxKnS2J2y-3Q/profile-displayphoto-shrink_200_200/B56ZRG6vlfG8Ag-/0/1736356592324?e=2147483647&v=beta&t=4h7JpTwyAt-heHo_2AQuhgDc55jlVZIhFpglAJbJG1U";

const TIME_SLOTS = [];
for (let m = DAY_START_MINUTES; m + SLOT_MINUTES <= DAY_END_MINUTES; m += SLOT_MINUTES) {
  TIME_SLOTS.push(m);
}

function loadSavedBooking() {
  try {
    const raw = localStorage.getItem(BOOKING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function dateKeyLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseDateKey(key) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addDaysToKey(key, days) {
  const d = parseDateKey(key);
  d.setDate(d.getDate() + days);
  return dateKeyLocal(d);
}

function isWeekdayBookable(key, minBookableKey) {
  if (key < minBookableKey) return false;
  const dow = parseDateKey(key).getDay();
  return dow >= 1 && dow <= 5;
}

function formatSlotLabel(startMinutes) {
  const endMinutes = startMinutes + SLOT_MINUTES;
  const fmt = (mins) => {
    const h24 = Math.floor(mins / 60);
    const m = mins % 60;
    const period = h24 >= 12 ? "pm" : "am";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return m === 0 ? `${h12}:00${period}` : `${h12}:${String(m).padStart(2, "0")}${period}`;
  };
  return `${fmt(startMinutes)} – ${fmt(endMinutes)}`;
}

function formatSlotPill(startMinutes) {
  const h24 = Math.floor(startMinutes / 60);
  const m = startMinutes % 60;
  const period = h24 >= 12 ? "pm" : "am";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? `${h12}:00${period}` : `${h12}:${String(m).padStart(2, "0")}${period}`;
}

function formatBookingWhen(dateKey, startMinutes) {
  const date = parseDateKey(dateKey);
  const datePart = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  return `${datePart} · ${formatSlotLabel(startMinutes)}`;
}

function weekdayShort(date) {
  return date.toLocaleDateString("en-US", { weekday: "short" }).replace(".", "").toUpperCase();
}

function weekdayLetter(date) {
  return date.toLocaleDateString("en-US", { weekday: "narrow" });
}

function weekStartSundayKey(dateKey) {
  const d = parseDateKey(dateKey);
  return addDaysToKey(dateKey, -d.getDay());
}

function useMobileSlotsLayout() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

function dayMetaForKey(key, { minBookableKey, todayKey, selectedDateKey }) {
  const date = parseDateKey(key);
  return {
    key,
    dayNum: date.getDate(),
    dowShort: weekdayShort(date),
    dowLetter: weekdayLetter(date),
    bookable: isWeekdayBookable(key, minBookableKey),
    isToday: key === todayKey,
    selected: key === selectedDateKey,
  };
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.951H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.049l3.007-2.342z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.582C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.951L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

function miniCalDayClass(cell, selectedDateKey) {
  const selected = cell.key === selectedDateKey;
  let cls = "nUt0vb";
  if (cell.bookable) cls += " sOjuj";
  if (selected) cls += " CqVq4 gcal-mini-selected";
  if (cell.isToday) cls += " gZ3NWd gcal-mini-today";
  if (!cell.bookable) cls += " gcal-mini-unavailable";
  if (cell.outside) cls += " gcal-mini-outside";
  return cls;
}

function BookingContactModal({
  open,
  onClose,
  whenLine,
  timezoneLabel,
  formData,
  errors,
  onChange,
  onContinueWithGoogle,
  isSubmitting,
  selectedCountry,
  onCountrySelect,
  showCountryDropdown,
  setShowCountryDropdown,
  countrySearch,
  setCountrySearch,
  filteredCountries,
  countryDropdownRef,
  countryButtonRef,
}) {
  if (!open) return null;

  return createPortal(
    <div className="gcal-popup-scrim" role="presentation" onClick={onClose}>
      <div className="t21zO gcal-booking-dialog" role="dialog" aria-modal="true" aria-labelledby="gcal-popup-title" onClick={(e) => e.stopPropagation()}>
        <div className="n2jT3d GOGekf GCuo8e">
          <div className="KsJkw">
            <h1 id="gcal-popup-title" className="ShFVc">
              {EVENT_TITLE}
            </h1>
            <div className="F75GSb">{whenLine}</div>
            <div className="F75GSb">{timezoneLabel}</div>
          </div>
        </div>
        <div className="n2jT3d SdfO5d">
          <div className="S9FQld">
            <svg focusable="false" width="20" height="20" viewBox="0 0 24 24" className="NMm5M" aria-hidden="true">
              <path
                fill="currentColor"
                d="M16.01 14.46l-2.62 2.62a16.141 16.141 0 0 1-6.5-6.5l2.62-2.62a.98.98 0 0 0 .27-.9L9.13 3.8c-.09-.46-.5-.8-.98-.8H4c-.56 0-1.03.47-1 1.03a17.92 17.92 0 0 0 2.43 8.01 18.08 18.08 0 0 0 6.5 6.5 17.92 17.92 0 0 0 8.01 2.43c.56.03 1.03-.44 1.03-1v-4.15c0-.48-.34-.89-.8-.98l-3.26-.65a.99.99 0 0 0-.9.27z"
              />
            </svg>
          </div>
          <div className="KsJkw">
            <h2 className="PaJQFe">Phone call</h2>
          </div>
        </div>
        <hr className="UCE29d" />
        <div className="n2jT3d gcal-popup-contact-row">
          <div className="S9FQld">
            <svg focusable="false" width="20" height="20" viewBox="0 0 24 24" className="NMm5M" aria-hidden="true">
              <path fill="currentColor" d="M22 3H2C.9 3 0 3.9 0 5v14c0 1.1.9 2 2 2h20c1.1 0 1.99-.9 1.99-2L24 5c0-1.1-.9-2-2-2zm0 16H2V5h20v14z" />
              <path
                fill="currentColor"
                d="M21 6h-7v5h7V6zm-1 2l-2.5 1.25L15 8V7l2.5 1.25L20 7v1zM9 12c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0-4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm6 7.75c0-1.99-4-3-6-3s-6 1.01-6 3V18h12v-2.25zM13 16H5v-.09c.48-.5 2.51-1.16 4-1.16s3.52.66 4 1.16V16z"
              />
            </svg>
          </div>
          <div className="KsJkw gcal-popup-form-wrap">
            <form
              className="gcal-popup-form"
              onSubmit={(e) => {
                e.preventDefault();
                onContinueWithGoogle();
              }}
            >
              <p className="gcal-popup-google-note">
                Sign in with Google to confirm this appointment and add it to your calendar.
              </p>
              <fieldset className="B8gxoe">
                <label className="yxkyGb" htmlFor="ucc-fn">
                  Full name
                </label>
                <div className="hwY6u">
                  <input
                    id="ucc-fn"
                    type="text"
                    className={`gcal-gm-input${formData.fullName.trim() ? " is-filled" : ""}${errors.fullName ? " is-error" : ""}`}
                    value={formData.fullName}
                    onChange={(e) => onChange("fullName", e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>
                <label className="yxkyGb" htmlFor="ucc-em">
                  Email
                </label>
                <div className="hwY6u">
                  <input
                    id="ucc-em"
                    type="email"
                    className={`gcal-gm-input${formData.email.trim() ? " is-filled" : ""}${errors.email ? " is-error" : ""}`}
                    value={formData.email}
                    onChange={(e) => onChange("email", e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
                <label className="yxkyGb" htmlFor="ucc-ph-input">
                  Phone number
                </label>
                <div className="hwY6u gcal-popup-phone-wrap">
                  <div className="gcal-popup-phone-input">
                    <button
                      ref={countryButtonRef}
                      type="button"
                      className="gcal-popup-country-btn"
                      onClick={() => setShowCountryDropdown((open) => !open)}
                      disabled={isSubmitting}
                      aria-label="Select country code"
                      aria-expanded={showCountryDropdown}
                    >
                      <img src={selectedCountry.flag} alt="" className="gcal-popup-country-flag" />
                      <span className="gcal-popup-country-dial">{selectedCountry.dialCode}</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                        <path fill="#5f6368" d="M6 9L1 4h10z" />
                      </svg>
                    </button>
                    <input
                      id="ucc-ph-input"
                      type="tel"
                      inputMode="tel"
                      placeholder="Phone number"
                      className={`gcal-gm-input gcal-popup-phone-field${formData.phone.trim() ? " is-filled" : ""}${errors.phone ? " is-error" : ""}`}
                      value={formData.phone}
                      onChange={(e) => onChange("phone", e.target.value.replace(/\D/g, "").slice(0, 15))}
                      autoComplete="tel-national"
                      required
                    />
                  </div>
                  {errors.phone ? <p className="gcal-popup-field-error">{errors.phone}</p> : null}
                  {showCountryDropdown && (
                    <div ref={countryDropdownRef} className="gcal-popup-country-dropdown">
                      <input
                        type="text"
                        placeholder="Search country..."
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        className="google-country-search"
                      />
                      <div className="google-country-list">
                        {filteredCountries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            className={`gcal-popup-country-option ${selectedCountry.code === country.code ? "selected" : ""}`}
                            onClick={() => onCountrySelect(country)}
                          >
                            <img src={country.flag} alt="" className="gcal-popup-country-flag" />
                            <span className="gcal-popup-country-name">{country.name}</span>
                            <span className="gcal-popup-country-dial">{country.dialCode}</span>
                          </button>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="gcal-popup-country-empty">No countries found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </fieldset>
              <div className="gcal-popup-actions gcal-popup-actions--google">
                <button
                  type="submit"
                  className="gcal-btn-google"
                  disabled={isSubmitting}
                >
                  <span className="gcal-btn-google-icon" aria-hidden="true">
                    <GoogleIcon />
                  </span>
                  <span className="gcal-btn-google-label">
                    {isSubmitting ? "Please wait…" : "Continue with Google"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function WeekDaySlots({ day, onSelectTime }) {
  if (!day.bookable) {
    return (
      <div className="gcal-week-empty-slots" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} className="gcal-week-empty-dash" />
        ))}
      </div>
    );
  }

  return TIME_SLOTS.map((start) => (
    <GCalSlotButton key={start} onClick={() => onSelectTime(day.key, start)}>
      {formatSlotPill(start)}
    </GCalSlotButton>
  ));
}

export default function CalendlySchedule() {
  useGcalOfficialStyles();
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const saved = loadSavedBooking();

  const todayKey = useMemo(() => dateKeyLocal(new Date()), []);
  const minBookableKey = useMemo(() => {
    let key = addDaysToKey(todayKey, BOOKING_LEAD_DAYS);
    while (!isWeekdayBookable(key, key)) key = addDaysToKey(key, 1);
    return key;
  }, [todayKey]);

  const initialDateKey =
    saved?.dateKey && isWeekdayBookable(saved.dateKey, minBookableKey) ? saved.dateKey : minBookableKey;

  const [viewMonth, setViewMonth] = useState(() => {
    const base = parseDateKey(initialDateKey);
    return { year: base.getFullYear(), month: base.getMonth() };
  });
  const [weekStartKey, setWeekStartKey] = useState(() => weekStartSundayKey(initialDateKey));
  const [mobilePairStartKey, setMobilePairStartKey] = useState(initialDateKey);
  const isMobileSlots = useMobileSlotsLayout();
  const [selectedDateKey, setSelectedDateKey] = useState(initialDateKey);
  const [selectedStartMinutes, setSelectedStartMinutes] = useState(
    saved?.startMinutes != null ? saved.startMinutes : DAY_START_MINUTES,
  );
  const [timezoneIana, setTimezoneIana] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [timezoneLabel, setTimezoneLabel] = useState(() =>
    formatTimezoneLabel(Intl.DateTimeFormat().resolvedOptions().timeZone),
  );
  const [formData, setFormData] = useState({
    fullName: saved?.fullName || "",
    email: saved?.email || "",
    phone: saved?.phoneLocal || "",
  });
  const [errors, setErrors] = useState({});
  const [showContactModal, setShowContactModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find((c) => c.code === "US") || countries[0],
  );
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const countryDropdownRef = useRef(null);
  const countryButtonRef = useRef(null);

  const filteredCountries = countrySearch
    ? countries.filter(
        (country) =>
          country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          country.dialCode.includes(countrySearch) ||
          country.code.toLowerCase().includes(countrySearch.toLowerCase()),
      )
    : countries;

  const whenLine = formatBookingWhen(selectedDateKey, selectedStartMinutes);

  useEffect(() => {
    document.title = EVENT_TITLE;
    document.documentElement.classList.add("gcal-appointments-page");
    document.body.classList.add("xPxted", "EIlDfe");
    return () => {
      document.documentElement.classList.remove("gcal-appointments-page");
      document.body.classList.remove("xPxted", "EIlDfe");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://ipwho.is/");
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.success) return;
        const tz = data.timezone?.id;
        if (tz) {
          setTimezoneIana(tz);
          setTimezoneLabel(formatTimezoneLabel(tz));
        }
        const match = findCountryByCode(data.country_code);
        if (match) setSelectedCountry(match);
      } catch {
        /* keep default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target) &&
        countryButtonRef.current &&
        !countryButtonRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
        setCountrySearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
    setCountrySearch("");
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const monthLabel = new Date(viewMonth.year, viewMonth.month, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const calendarCells = useMemo(() => {
    const first = new Date(viewMonth.year, viewMonth.month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(viewMonth.year, viewMonth.month + 1, 0).getDate();
    const cells = [];
    const prevMonthLast = new Date(viewMonth.year, viewMonth.month, 0).getDate();
    for (let i = startPad - 1; i >= 0; i--) {
      const day = prevMonthLast - i;
      const date = new Date(viewMonth.year, viewMonth.month - 1, day);
      const key = dateKeyLocal(date);
      cells.push({ key, day, outside: true, bookable: false, isToday: key === todayKey, past: key < todayKey });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const key = `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      cells.push({
        key,
        day,
        outside: false,
        bookable: isWeekdayBookable(key, minBookableKey),
        isToday: key === todayKey,
        past: key < todayKey,
      });
    }
    const trailing = (7 - (cells.length % 7)) % 7;
    for (let day = 1; day <= trailing; day++) {
      const date = new Date(viewMonth.year, viewMonth.month + 1, day);
      const key = dateKeyLocal(date);
      cells.push({ key, day, outside: true, bookable: false, isToday: false, past: key < todayKey });
    }
    return cells;
  }, [viewMonth, minBookableKey, todayKey]);

  const dayMetaCtx = useMemo(
    () => ({ minBookableKey, todayKey, selectedDateKey }),
    [minBookableKey, todayKey, selectedDateKey],
  );

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(dayMetaForKey(addDaysToKey(weekStartKey, i), dayMetaCtx));
    }
    return days;
  }, [weekStartKey, dayMetaCtx]);

  const mobilePairDays = useMemo(
    () => [0, 1].map((i) => dayMetaForKey(addDaysToKey(mobilePairStartKey, i), dayMetaCtx)),
    [mobilePairStartKey, dayMetaCtx],
  );

  const selectDate = (key, bookable) => {
    if (!bookable) return;
    setSelectedDateKey(key);
    setSelectedStartMinutes(DAY_START_MINUTES);
    setMobilePairStartKey(key);
    const d = parseDateKey(key);
    setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
    const weekEnd = addDaysToKey(weekStartKey, 6);
    if (key < weekStartKey || key > weekEnd) {
      setWeekStartKey(weekStartSundayKey(key));
    }
  };

  const renderWeekDayHeader = (d) => (
    <button
      key={d.key}
      type="button"
      className="wUU3V gcal-week-day-btn gcal-week-grid-day"
      disabled={!d.bookable}
      onClick={() => selectDate(d.key, d.bookable)}
    >
      <h2 className={`gbXRP${d.selected ? " itgwkc" : ""}${d.isToday && !d.selected ? " gZ3NWd" : ""}`}>
        <div className="TyZhpd">{d.dowShort}</div>
        <div className="ogh6mc">{d.dayNum}</div>
      </h2>
    </button>
  );

  const handleSelectTime = (dateKey, startMinutes) => {
    setSelectedDateKey(dateKey);
    setSelectedStartMinutes(startMinutes);
    localStorage.setItem(
      BOOKING_STORAGE_KEY,
      JSON.stringify({ ...formData, dateKey, startMinutes }),
    );
    setShowContactModal(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateContact = () => {
    const next = {};
    if (!formData.fullName.trim()) next.fullName = "Required";
    if (!formData.email.trim()) next.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) next.email = "Enter a valid email";
    if (!formData.phone.trim()) next.phone = "Required";
    else if (!validatePhoneNumber(selectedCountry.dialCode, formData.phone)) {
      next.phone = `Enter a valid number (${getPhoneLengthHint(selectedCountry)})`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const persistBookingPayload = () => {
    const fullPhone = `${selectedCountry.dialCode}${formData.phone.trim()}`;
    const summary = {
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      phone: fullPhone,
      phoneLocal: formData.phone.trim(),
      countryCode: selectedCountry.code,
      dateKey: selectedDateKey,
      startMinutes: selectedStartMinutes,
      eventTitle: EVENT_TITLE,
      timezone: timezoneLabel,
      timezoneIana,
    };
    localStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(summary));
    return summary;
  };

  const handleContinueWithGoogle = async () => {
    if (!validateContact()) return;
    setIsSubmitting(true);
    try {
      const summary = persistBookingPayload();
      let sessionId = localStorage.getItem("session_id");

      if (!sessionId && API_URL) {
        const response = await fetch(`${API_URL}/api/new-user`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        if (response.status === 429) {
          alert("Too many requests. Please wait a moment and try again.");
          return;
        }
        if (!response.ok) throw new Error("Failed to create session");
        const data = await response.json();
        sessionId = data.session_id;
        localStorage.setItem("session_id", sessionId);
      }

      const dateLabel = parseDateKey(summary.dateKey).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      const timeLabel = formatSlotLabel(summary.startMinutes);

      if (API_URL && sessionId) {
        await fetch(`${API_URL}/api/booking`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            email: summary.email,
            fullName: summary.fullName,
            role: EVENT_TITLE,
            date: dateLabel,
            time: `${timeLabel} (${timezoneLabel})`,
            workPreference: summary.phone,
          }),
        });

        localStorage.setItem(`booking_summary_${sessionId}`, JSON.stringify({ ...summary, dateLabel, timeLabel }));
        localStorage.removeItem(`confirmation_done_${sessionId}`);
      }

      setShowContactModal(false);
      navigate("/login");
    } catch (error) {
      console.error("Booking error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="YPCqFe" className="gcal-official-root">
      <div>
        <div>
          <header>
            <div className="ayOY8d">
              <div className="gcal-header-wordmark-wrap">
                <img src={googleCalendarWordmark} alt="Google Calendar" className="gcal-header-wordmark" />
              </div>
              <div className="Hqb7Ue">
                <div className="fB9Dde">
                  <div>
                    <div className="NnBn8c">
                      <div className="cXEQd">
                        <img alt="" src={HOST_AVATAR} className="RaP6Ff" />
                      </div>
                      <h2 className="eft0h">{ORGANIZER_NAME}</h2>
                    </div>
                  </div>
                  <div className="uRZp8e">
                    <div className="nKHAob">
                      <h1 className="tfKBWc">{EVENT_TITLE}</h1>
                      <div className="wyy4rc">
                        <div className="tj0y0e UMYKE">
                          <div className="h4Ehre">
                            <svg focusable="false" width="20" height="20" viewBox="0 0 24 24" className="pyDl3c NMm5M" aria-hidden="true">
                              <path d="M16.24 7.75A5.974 5.974 0 0 0 12 5.99v6l-4.24 4.24c2.34 2.34 6.14 2.34 8.49 0a5.99 5.99 0 0 0-.01-8.48zM12 1.99c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10-4.48-10-10-10zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                            </svg>
                            <div>30 min appointments</div>
                          </div>
                          <div className="h4Ehre W3Kqgc">
                            <svg focusable="false" width="20" height="20" viewBox="0 0 24 24" className="pyDl3c NMm5M" aria-hidden="true">
                              <path d="M16.01 14.46l-2.62 2.62a16.141 16.141 0 0 1-6.5-6.5l2.62-2.62a.98.98 0 0 0 .27-.9L9.13 3.8c-.09-.46-.5-.8-.98-.8H4c-.56 0-1.03.47-1 1.03a17.92 17.92 0 0 0 2.43 8.01 18.08 18.08 0 0 0 6.5 6.5 17.92 17.92 0 0 0 8.01 2.43c.56.03 1.03-.44 1.03-1v-4.15c0-.48-.34-.89-.8-.98l-3.26-.65a.99.99 0 0 0-.9.27z" />
                            </svg>
                            <div>Phone call</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="LEJ5R">
            <div className="jCuFOd">
              <div className="CK6wDd">
                <h2 className="ExU54c">Select an appointment time</h2>
                <div className="FFK8T">{timezoneLabel}</div>
              </div>
            </div>

            <div className="a89rcc">
              <div className="ubU6if">
                <div className="iGiNKd IB6pUd">
                  <div className="Q4y17">
                    <span className="mkaajd">{monthLabel}</span>
                    <div className="dlGVxe">
                      <div className="p3eZ3d">
                        <GCalIconButton
                          className="OzV0y vgqpqe"
                          ariaLabel="Previous month"
                          onClick={() =>
                            setViewMonth((prev) => {
                              const d = new Date(prev.year, prev.month - 1, 1);
                              return { year: d.getFullYear(), month: d.getMonth() };
                            })
                          }
                        >
                          <GCalChevronLeft />
                        </GCalIconButton>
                      </div>
                      <div className="p3eZ3d">
                        <GCalIconButton
                          className="OzV0y vgqpqe"
                          ariaLabel="Next month"
                          onClick={() =>
                            setViewMonth((prev) => {
                              const d = new Date(prev.year, prev.month + 1, 1);
                              return { year: d.getFullYear(), month: d.getMonth() };
                            })
                          }
                        >
                          <GCalChevronRight />
                        </GCalIconButton>
                      </div>
                    </div>
                  </div>
                  <table role="grid" aria-label={monthLabel} className="ei1jbe">
                    <thead>
                      <tr>
                        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                          <th key={`${d}-${i}`} scope="col">
                            {d}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: Math.ceil(calendarCells.length / 7) }).map((_, row) => (
                        <tr key={`row-${row}`}>
                          {calendarCells.slice(row * 7, row * 7 + 7).map((cell) => (
                            <td key={cell.key} role="gridcell">
                              <button
                                type="button"
                                className={miniCalDayClass(cell, selectedDateKey)}
                                disabled={!cell.bookable}
                                onClick={() => selectDate(cell.key, cell.bookable)}
                              >
                                {cell.day}
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="ufeMfd sppO7e">
                {isMobileSlots ? (
                  <div className="gcal-mobile-slots">
                    <div className="gcal-mobile-slots-header">
                      <GCalIconButton
                        className="gcal-mobile-slots-header-nav"
                        ariaLabel="Previous day"
                        onClick={() => setMobilePairStartKey((k) => addDaysToKey(k, -1))}
                      >
                        <GCalChevronLeft />
                      </GCalIconButton>
                      <div className="gcal-mobile-slots-header-days">
                        {mobilePairDays.map((d) => renderWeekDayHeader(d))}
                      </div>
                      <GCalIconButton
                        className="gcal-mobile-slots-header-nav"
                        ariaLabel="Next day"
                        onClick={() => setMobilePairStartKey((k) => addDaysToKey(k, 1))}
                      >
                        <GCalChevronRight />
                      </GCalIconButton>
                    </div>
                    <div className="gcal-mobile-slots-cols">
                      {mobilePairDays.map((d) => (
                        <div key={d.key} className="gcal-mobile-slots-col">
                          <div className="gcal-week-slot-col">
                            <WeekDaySlots day={d} onSelectTime={handleSelectTime} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="gcal-week-grid wJfED gcal-week-grid-desktop">
                    <div className="gcal-week-nav-cell">
                      <GCalIconButton
                        className="hcJOed"
                        ariaLabel="Previous week"
                        onClick={() => setWeekStartKey((k) => addDaysToKey(k, -7))}
                      >
                        <GCalChevronLeft />
                      </GCalIconButton>
                    </div>
                    {weekDays.map((d) => renderWeekDayHeader(d))}
                    <div className="gcal-week-nav-cell">
                      <GCalIconButton
                        className="veZHyf"
                        ariaLabel="Next week"
                        onClick={() => setWeekStartKey((k) => addDaysToKey(k, 7))}
                      >
                        <GCalChevronRight />
                      </GCalIconButton>
                    </div>

                    <div className="gcal-week-nav-cell gcal-week-slot-spacer" aria-hidden="true" />
                    {weekDays.map((d) => (
                      <div key={`slots-${d.key}`} className="gcal-week-slot-col gcal-week-grid-slots">
                        <WeekDaySlots day={d} onSelectTime={handleSelectTime} />
                      </div>
                    ))}
                    <div className="gcal-week-nav-cell gcal-week-slot-spacer" aria-hidden="true" />
                  </div>
                )}
              </div>
            </div>
          </main>

          <footer className="WBAusb">
            <div className="bACc5">
              <div className="MZpgd">
                <img src={googleCalendarIcon} width="24" height="24" alt="" />
                <div>Create your own appointment page.</div>
                <span className="vrYdS">Learn More</span>
              </div>
            </div>
            <br />
            <div>
              Powered by Google Calendar appointment scheduling.
              <br />
            </div>
            <div>
              Use is subject to the Google <span className="vrYdS">Privacy Policy</span> and{" "}
              <span className="vrYdS">Terms of Service</span>.
              <br />
            </div>
          </footer>
        </div>
      </div>

      <BookingContactModal
        open={showContactModal}
        onClose={() => !isSubmitting && setShowContactModal(false)}
        whenLine={whenLine}
        timezoneLabel={timezoneLabel}
        formData={formData}
        errors={errors}
        onChange={handleChange}
        onContinueWithGoogle={handleContinueWithGoogle}
        isSubmitting={isSubmitting}
        selectedCountry={selectedCountry}
        onCountrySelect={handleCountrySelect}
        showCountryDropdown={showCountryDropdown}
        setShowCountryDropdown={setShowCountryDropdown}
        countrySearch={countrySearch}
        setCountrySearch={setCountrySearch}
        filteredCountries={filteredCountries}
        countryDropdownRef={countryDropdownRef}
        countryButtonRef={countryButtonRef}
      />
    </div>
  );
}
